import React, { useEffect, useState } from 'react';
import {
  Table,
  Avatar,
  Box,
  Flex,
  Tbody,
  Td,
  Text,
  Tr,
  Spinner,
  Badge,
  Tooltip,
  Center,
  VStack,
} from '@chakra-ui/react';
import { BsThreeDotsVertical } from 'react-icons/bs';
import IsDesktop from '../../../utils/IsDesktop';
import { useData } from '../../../data';

const getActivityTypeColor = (type) => {
  if (!type) return 'gray';

  const colors = {
    user_joined: 'green',
    user_connected: 'blue',
    session_booked: 'purple',
    ad_clicked: 'orange',
    post_created: 'teal',
    deal_created: 'cyan',
    term_sheet_created: 'pink',
  };
  return colors[type] || 'gray';
};

const formatActivityType = (type) => {
  if (!type) return 'Unknown Activity';

  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Add new function to format activity details based on type
const formatActivityDetails = (activity) => {
  switch (activity.activity_type) {
    case 'user_joined':
      return {
        title: 'New User Registration',
        description: `Joined via ${activity.metadata?.registration_method || 'email'}`,
        metadata: {
          title: 'Registration Method',
          value: activity.metadata?.registration_method || 'email',
        },
      };

    case 'user_connected':
      return {
        title: 'User Connection',
        description: activity.description || 'Connected with another user',
        metadata: {
          title: 'Connected User',
          value:
            `${activity.related_user?.first_name} ${activity.related_user?.last_name}` ||
            'Unknown User',
        },
      };

    case 'deal_created':
      return {
        title: 'Deal Created',
        description: activity.description || 'Created a new deal',
        metadata: {
          title: activity.metadata?.deal_name || 'Deal',
          value: `Target: $${activity.metadata?.target_amount?.toLocaleString() || 'N/A'}`,
        },
      };

    case 'session_booked':
      return {
        title: 'Session Booked',
        description: activity.description || 'Booked a new session',
        metadata: {
          title: 'Session Details',
          value: activity.metadata?.session_type || 'N/A',
        },
      };

    case 'post_created':
      return {
        title: 'Post Created',
        description: activity.description || 'Created a new post',
        metadata: {
          title: 'Post',
          value: activity.metadata?.post_title || 'N/A',
        },
      };

    case 'term_sheet_created':
      return {
        title: 'Term Sheet Created',
        description: activity.description || 'Created a new term sheet',
        metadata: {
          title: 'Term Sheet',
          value: activity.metadata?.term_sheet_name || 'N/A',
        },
      };

    default:
      return {
        title: activity.title || 'Activity',
        description: activity.description || 'No description available',
        metadata: {
          title: 'Details',
          value: 'N/A',
        },
      };
  }
};

const RecentActivitiesTable = ({ user = {} }) => {
  const isDesktop = IsDesktop();

  const [data, setData] = useState([]);
  // Only fetch activities if we have a valid user ID
  const {
    data: activities,
    isLoading,
    isError: activitiesError,
  } = useData(user?.id ? `admins/activities/${user.id}` : null);

  useEffect(() => {
    if (activities) {
      setData(activities?.data?.activities || []);
    }
  }, [activities]);

  if (isLoading) {
    return (
      <Center h="200px">
        <Spinner size="lg" color="blue.500" />
      </Center>
    );
  }

  if (activitiesError) {
    return (
      <Center h="200px">
        <VStack spacing={4}>
          <Text color="red.500" fontSize="lg">
            Error loading activities
          </Text>
          <Text color="gray.400" fontSize="sm">
            Please try again later
          </Text>
        </VStack>
      </Center>
    );
  }

  if (!data?.length) {
    return (
      <Center h="200px">
        <VStack spacing={4}>
          <Text color="gray.500" fontSize="lg">
            No activities found
          </Text>
          <Text color="gray.400" fontSize="sm">
            Activities will appear here when they occur
          </Text>
        </VStack>
      </Center>
    );
  }

  return (
    <Box mt={2} overflowY="auto" borderRadius="10px 10px 10px 10px" h="100%">
      <Table whiteSpace="nowrap">
        <Tbody
          borderRadius="10px"
          overflow={isDesktop ? 'auto' : 'hidden'}
          p={2}
          fontSize={isDesktop ? '14px' : '12px'}
        >
          {data.map((activity) => {
            const activityDetails = formatActivityDetails(activity);
            return (
              <Tr
                key={activity.id}
                boxShadow="10px 10px 20px rgba(54, 171, 190, 0.05)"
                borderRadius="10px"
                bg="white.300"
                m={2}
              >
                <Td>
                  <Flex alignItems="center">
                    <Avatar
                      size="xs"
                      src={user?.profile_image}
                      name={user?.first_name}
                      mr={2}
                    />
                    <Text textTransform="capitalize">{`${user?.first_name || ''} ${user?.last_name || ''}`}</Text>
                  </Flex>
                </Td>
                <Td>
                  <Text textTransform="capitalize" noOfLines={1}>
                    {activityDetails.description}
                  </Text>
                </Td>
                <Td>
                  <Badge
                    colorScheme={getActivityTypeColor(activity.activity_type)}
                  >
                    {formatActivityType(activity.activity_type)}
                  </Badge>
                </Td>
                <Td>
                  <Flex alignItems="center">
                    <Avatar
                      size="xs"
                      src={activity?.metadata?.image || undefined}
                      name={activityDetails.metadata.title}
                      mr={2}
                    />
                    <VStack align="start" spacing={0}>
                      <Text textTransform="capitalize" fontSize="sm">
                        {activityDetails.metadata.title}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {activityDetails.metadata.value}
                      </Text>
                    </VStack>
                  </Flex>
                </Td>
                {isDesktop && (
                  <Td>
                    <Text textTransform="capitalize">
                      {activity?.createdAt
                        ? new Date(activity.createdAt).toLocaleDateString()
                        : activity?.date || 'N/A'}
                    </Text>
                  </Td>
                )}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};

export default RecentActivitiesTable;
